import { createRouter, createWebHistory } from 'vue-router'

import { ensureSession, resetSession } from '@/libs/auth'
import { safeRedirect } from '@/libs/helpers'
import { ensureImageStatus } from '@/libs/image'
import { useAppStore } from '@/stores/app'

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition)         return savedPosition
        if (to.hash)               return { el: to.hash }
        if (from.path === to.path) return {}
        return { top: 0, behavior: 'smooth' }
    },
    routes: [
        {
            path:     '/',
            redirect: '/home',
            meta:     { requiresAuth: true },
        },
        {
            path:      '/login',
            name:      'login',
            component: () => import('@/views/Login.vue'),
            meta:      { layout: 'Blank', requiresAuth: false },
        },
        {
            path:      '/preparing',
            name:      'preparing',
            component: () => import('@/views/Preparing.vue'),
            meta:      { layout: 'Blank', requiresAuth: true },
        },
        {
            path:      '/home',
            name:      'home',
            component: () => import('@/views/home/Home.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:      '/workspaces',
            name:      'workspaces',
            component: () => import('@/views/environment/Environments.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:      '/environments/:id',
            name:      'environment',
            component: () => import('@/views/environment/Environment.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:      '/environments/:id/console',
            name:      'environment-console',
            component: () => import('@/views/environment/Console.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:      '/environments/:id/files',
            name:      'environment-files',
            component: () => import('@/views/environment/Files.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:      '/environments/:id/changes',
            name:      'environment-changes',
            component: () => import('@/views/environment/Changes.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:      '/settings',
            name:      'settings',
            component: () => import('@/views/settings/Settings.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:      '/admin/host',
            name:      'admin-host',
            component: () => import('@/views/admin/Host.vue'),
            meta:      { requiresAuth: true, requiresAdmin: true },
        },
        {
            path:      '/admin/hostnames',
            name:      'admin-hostnames',
            component: () => import('@/views/admin/Hostnames.vue'),
            meta:      { requiresAuth: true, requiresAdmin: true },
        },
        {
            path:      '/admin/tls',
            name:      'admin-tls',
            component: () => import('@/views/admin/Tls.vue'),
            meta:      { requiresAuth: true, requiresAdmin: true },
        },
        {
            path:      '/admin/environments',
            name:      'admin-environments',
            component: () => import('@/views/admin/Environments.vue'),
            meta:      { requiresAuth: true, requiresAdmin: true },
        },
        {
            path:      '/admin/users',
            name:      'admin-users',
            component: () => import('@/views/admin/Users.vue'),
            meta:      { requiresAuth: true, requiresAdmin: true },
        },
        {
            path:      '/admin/settings',
            name:      'admin-settings',
            component: () => import('@/views/admin/AdminSettings.vue'),
            meta:      { requiresAuth: true, requiresAdmin: true },
        },
        {
            path:      '/admin/audit',
            name:      'admin-audit',
            component: () => import('@/views/admin/AuditEvents.vue'),
            meta:      { requiresAuth: true, requiresAdmin: true },
        },
        {
            path:      '/:pathMatch(.*)*',
            name:      'not-found',
            component: () => import('@/views/NotFound.vue'),
            meta:      { requiresAuth: true },
        },
    ],
})

router.beforeEach(async (to, _from, next) => {
    const appStore = useAppStore()
    const loggedIn = await ensureSession()

    if (loggedIn && to.name === 'login') return next({ name: 'home' })

    if (to.meta.requiresAuth && !loggedIn) {
        appStore.setLogout()
        resetSession()
        return next({ name: 'login', query: { redirect: to.fullPath } })
    }

    if (to.meta.requiresAdmin && !appStore.isAdmin()) return next({ name: 'home' })

    // THE BOOT GATE. While the plane is building the workspace image there is nothing in
    // Startbox to use — no workspace can be created from an image that does not exist yet —
    // so the app is the preparing page and only the preparing page, administrators included
    // (invariant 27). The route that was asked for travels in `?redirect=` and the page
    // itself opens it the moment the build ends, so nobody has to find their way back.
    //
    // One check, here, rather than a condition on every page: a signed-in navigation is the
    // only way into any of them.
    const preparing = to.meta.requiresAuth ? await ensureImageStatus() : false

    if (preparing && to.name !== 'preparing') {
        return next({ name: 'preparing', query: { redirect: to.fullPath } })
    }

    // A build that has finished — or failed, where the plane keeps the image it had — leaves
    // the page with nothing to wait for, including on a reload of its own URL.
    if (!preparing && to.name === 'preparing') return next(safeRedirect(to.query.redirect))

    next()
})

export default router
